import template from "./library-costs-page.html";

const LibraryCostsPageComponent = {
  template: template,
  controller: class CostListViewController {
    /* @ngInject */
    constructor($rootScope, $q, $filter, CostFolder, Cost, ModalsManager, Notification, SweetAlertService) {
      this.$rootScope = $rootScope;
      this.$filter = $filter;
      this.$q = $q;
      this.CostFolder = CostFolder;
      this.Cost = Cost;
      this.ModalsManager = ModalsManager;
      this.NotificationService = Notification;
      this.SweetAlertService = SweetAlertService;
    }

    async $onInit() {
      this.rootFolder = {
        _id: null,
        name: this.$filter("trans")("costFolder.main"),
      };
      this.parentCostFolder = {};
      this.folders = [];
      this.costs = [];
      this.costsCount = 0;
      this.searchOptions = { name: "", page: 1 };

      await this.refresh(this.rootFolder);
      this.$rootScope.$applyAsync();

      this.$rootScope.$watch(
        () => this.searchOptions.name,
        (newVal, oldVal) => {
          if (newVal !== oldVal) {
            if (newVal.length === 0) this.refresh(this.rootFolder).then();
            else this.findBySearchText().then();
          }
        },
      );
    }

    async findBySearchText() {
      this.loading = true;
      this.searchOptions.page = 1;
      const data = await this.$q
        .all({
          costFolderPromise: this.CostFolder.query({
            q: this.searchOptions.name,
          }),
          costPromise: this.Cost.query({
            q: this.searchOptions.name,
            page: this.searchOptions.page,
          }),
          costsCountPromise: this.Cost.getCount({ q: this.searchOptions.name }),
        })
        .then((values) => {
          return {
            folders: values.costFolderPromise,
            costs: values.costPromise,
            costsCount: values.costsCountPromise,
          };
        });
      this.folders = data.folders;
      this.costs = data.costs;
      this.costsCount = data.costsCount;
      this.loading = false;
      this.$rootScope.$applyAsync();
    }

    async refresh(parent) {
      this.loading = true;
      this.searchOptions = { name: "", page: 1 };
      const data = await this.$q
        .all({
          costFolderPromise: this.CostFolder.queryByParent(parent._id),
          costPromise: this.Cost.queryByParent(parent._id, this.searchOptions.page),
          costsCountPromise: this.Cost.getCount({
            parent: parent._id || "null",
          }),
        })
        .then((values) => {
          return {
            folders: values.costFolderPromise,
            costs: values.costPromise,
            costsCount: values.costsCountPromise,
          };
        });
      this.parentCostFolder = parent;
      this.folders = data.folders;
      this.costs = data.costs;
      this.costsCount = data.costsCount;
      this.loading = false;
      this.$rootScope.$applyAsync();
    }

    async loadMoreCosts() {
      const loadedCosts = this.costs.length;
      const costsToLoad = this.costsCount;
      if (loadedCosts < costsToLoad && !this.loading) {
        this.searchOptions.page++;
        const costs = await this.loadCosts();
        this.costs = this.costs.concat(costs);
        this.$rootScope.$applyAsync();
      }
    }

    async loadCosts() {
      this.loading = true;
      let costs = [];

      if (this.searchOptions.name === "")
        costs = await this.Cost.queryByParent(this.parentCostFolder._id, this.searchOptions.page);
      else
        costs = await this.Cost.query({
          q: this.searchOptions.name,
          page: this.searchOptions.page,
        });

      this.loading = false;
      this.$rootScope.$applyAsync();
      return costs;
    }

    async loadCostFolder(parentCostFolderId) {
      if (parentCostFolderId) {
        const parentCostFolder = await this.CostFolder.get(parentCostFolderId);
        await this.refresh(parentCostFolder);
        this.parentCostFolder = parentCostFolder;
      } else {
        await this.refresh(this.rootFolder);
      }
      this.$rootScope.$applyAsync();
    }

    async createCostFolder() {
      if (await this.ModalsManager.openCostFolderModal(new this.CostFolder({ parent: this.parentCostFolder._id }))) {
        await this.loadCostFolder(this.parentCostFolder._id);
      }
    }

    async editCostFolder(folder) {
      if (await this.ModalsManager.openCostFolderModal(folder)) {
        await this.loadCostFolder(this.parentCostFolder._id);
      }
    }

    async moveCostFolder(folder) {
      if (await this.ModalsManager.openMoveCostModal(folder)) await this.loadCostFolder(this.parentCostFolder._id);
    }

    async deleteCostFolder(folder) {
      const costFolders = await this.CostFolder.getChildren(folder._id);
      if (costFolders.length > 0 && !(await this.SweetAlertService.canHaveAssociatedTariffs())) return;

      await folder.delete();
      this.NotificationService.success({
        title: this.$filter("trans")("notificationService.title.success"),
        message: this.$filter("trans")("notificationService.message.costFolderDeleted"),
      });
      await this.loadCostFolder(this.parentCostFolder._id);
      this.$rootScope.$applyAsync();
    }

    async createCost() {
      if (await this.ModalsManager.openCostModal(new this.Cost({ parent: this.parentCostFolder._id }))) {
        await this.loadCostFolder(this.parentCostFolder._id);
      }
    }

    async editCost(cost) {
      if (await this.ModalsManager.openCostModal(cost)) await this.loadCostFolder(this.parentCostFolder._id);
    }

    async moveCost(cost) {
      if (await this.ModalsManager.openMoveCostModal(cost)) await this.loadCostFolder(this.parentCostFolder._id);
    }

    async deleteCost(cost) {
      if (!(await this.SweetAlertService.costAssociatedToTariffs())) return;

      await cost.delete();
      this.NotificationService.success({
        title: this.$filter("trans")("notificationService.title.success"),
        message: this.$filter("trans")("notificationService.message.costDeleted"),
      });
      await this.loadCostFolder(this.parentCostFolder._id);
    }

    async openImportCostsCsvModal() {
      if (await this.ModalsManager.openImportCostsCsvModal()) await this.refresh(this.rootFolder);
    }
  },
};

export default LibraryCostsPageComponent;
